import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api';
import { INBOX_SOURCES, inboxApi, type InboxDetail, type InboxListResult, type InboxRow, type InboxSource, type InboxStatus } from '../api/inbox';
import { tasksApi, type TaskBusiness } from '../api/tasks';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { fmtWhen, statusLabel } from './tasks.format';
import './inbox.css';

const PAGE_SIZE = 50;
type Chip = 'open' | 'new' | 'needs_human';
const CHIP_STATUSES: Record<Chip, InboxStatus[]> = { open: ['new', 'needs_human'], new: ['new'], needs_human: ['needs_human'] };

function badgeClass(s: InboxStatus): string {
  return s === 'needs_human' ? 'badge warn' : s === 'done' ? 'badge ok' : 'badge';
}

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
  return code === 'inbox_item_not_found' ? t('inbox.notFound') : code || t('common.error');
}

function selectedFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('id');
}

/** Where a knowledge outcome opens on «Знання» (ADR-0010); outcomeRef is that row's id. */
const KNOWLEDGE_PARAM: Partial<Record<string, string>> = { finding: 'finding', funnel_case: 'funnel', knowledge_source: 'source' };

const isImage = (mime: string | undefined) => !!mime && mime.startsWith('image/') && mime !== 'image/heic';

/**
 * Вхідна (ADR-0008): only what is not sorted yet and what waits for the human. Collectors fill it,
 * the scheduled triager empties it; the human answers the triager's questions here.
 */
export function Inbox({ canWrite }: { canWrite: boolean }) {
  const [businesses, setBusinesses] = useState<TaskBusiness[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [source, setSource] = useState('');
  const [chip, setChip] = useState<Chip>('open');
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState<InboxListResult | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(selectedFromUrl);
  const [detail, setDetail] = useState<InboxDetail | null>(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    tasksApi.businesses().then(setBusinesses).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [businessId, source, chip]);

  useEffect(() => {
    setError(null);
    const mine = ++seq.current;
    inboxApi
      .list({ businessId: businessId || undefined, source: (source || undefined) as InboxSource | undefined, status: CHIP_STATUSES[chip], page, pageSize: PAGE_SIZE })
      .then((r) => {
        if (mine === seq.current) setResult(r);
      })
      .catch((e) => {
        if (mine === seq.current) setError(errorText(e));
      });
  }, [businessId, source, chip, page, reload]);

  // The selection lives in the address (`?id=`) so a task born from an item can link straight to it.
  useEffect(() => {
    window.history.replaceState({}, '', selectedId ? `/inbox?id=${encodeURIComponent(selectedId)}` : '/inbox');
    setDetail(null);
    setAnswer('');
    if (selectedId) inboxApi.get(selectedId).then(setDetail).catch((e) => setError(errorText(e)));
  }, [selectedId]);

  async function act(run: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await run();
      setSelectedId(null);
      setReload((n) => n + 1);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<Column<InboxRow>[]>(
    () => [
      {
        key: 'what',
        title: t('inbox.col.what'),
        wrap: true,
        render: (i) => (
          <span className="inbox-what">
            {isImage(i.file?.mimeType) && <img className="inbox-thumb" src={i.file!.path} alt="" loading="lazy" />}
            <span>
              <a
                href={`/inbox?id=${encodeURIComponent(i.id)}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  setSelectedId(i.id);
                  window.scrollTo({ top: 0 });
                }}
              >
                {i.title ?? t('inbox.noTitle')}
              </a>
              {i.status === 'needs_human' && i.triageNote && (
                <>
                  <br />
                  <span className="muted">{i.triageNote}</span>
                </>
              )}
            </span>
          </span>
        ),
      },
      { key: 'source', title: t('inbox.col.source'), render: (i) => t(`inbox.source.${i.source}`) },
      { key: 'business', title: t('inbox.col.business'), render: (i) => i.business?.name ?? t('inbox.noBusiness') },
      { key: 'status', title: t('inbox.col.status'), render: (i) => <span className={badgeClass(i.status)}>{t(`inbox.status.${i.status}`)}</span> },
      { key: 'captured', title: t('inbox.col.captured'), render: (i) => <span className="muted">{fmtWhen(i.capturedAt)}</span> },
    ],
    [],
  );

  const total = result?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const counts = result?.counts;

  return (
    <div className="inbox">
      <h1>{t('inbox.title')}</h1>
      <p className="muted">{t('inbox.intro')}</p>
      {counts && <p>{t('inbox.summary', { new: counts.new, needs: counts.needs_human, sorted: counts.sortedToday })}</p>}
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}

      {detail && (
        <section className="card" style={{ margin: 'var(--sp-4) 0' }} aria-label={detail.title ?? t('inbox.noTitle')}>
          <div className="row between">
            <h2 style={{ margin: 0, overflowWrap: 'anywhere' }}>{detail.title ?? t('inbox.noTitle')}</h2>
            <button type="button" className="btn secondary small" onClick={() => setSelectedId(null)}>{t('inbox.detail.close')}</button>
          </div>
          <p className="muted">
            {t(`inbox.source.${detail.source}`)}
            {detail.business ? ` · ${detail.business.name}` : ''} · {t('inbox.detail.captured', { when: fmtWhen(detail.capturedAt) })} ·{' '}
            <span className={badgeClass(detail.status)}>{t(`inbox.status.${detail.status}`)}</span>
          </p>
          {detail.file &&
            (isImage(detail.file.mimeType) ? (
              <a href={detail.file.path} target="_blank" rel="noreferrer">
                <img className="inbox-preview" src={detail.file.path} alt={detail.title ?? ''} />
              </a>
            ) : (
              <p>
                <a href={detail.file.path} target="_blank" rel="noreferrer">{t('inbox.detail.file', { name: detail.file.filename })}</a>
              </p>
            ))}
          {detail.url && (
            <p style={{ overflowWrap: 'anywhere' }}>
              <a href={detail.url} target="_blank" rel="noreferrer">{t('inbox.detail.open')}</a> <span className="muted">{detail.url}</span>
            </p>
          )}
          {detail.text && (
            <>
              <p><b>{t('inbox.detail.text')}</b></p>
              <pre className="codeblock">{detail.text}</pre>
            </>
          )}
          {detail.triageNote && (
            <>
              <p><b>{detail.status === 'needs_human' ? t('inbox.detail.question') : t('inbox.detail.note')}</b></p>
              <p className="inbox-question">{detail.triageNote}</p>
            </>
          )}
          {detail.outcome && (
            <p>
              {t('inbox.detail.result', { outcome: t(`inbox.outcome.${detail.outcome}`) })}
              {detail.outcomeRef && KNOWLEDGE_PARAM[detail.outcome] ? (
                <>
                  {' · '}
                  <Link to={`/knowledge?${KNOWLEDGE_PARAM[detail.outcome]}=${encodeURIComponent(detail.outcomeRef)}`}>{t('inbox.detail.openKnowledge')}</Link>
                </>
              ) : detail.outcomeRef ? (
                ` · ${detail.outcomeRef}`
              ) : (
                ''
              )}
            </p>
          )}
          {detail.tasks.length > 0 && (
            <>
              <p><b>{t('inbox.detail.tasks')}</b></p>
              {detail.tasks.map((task) => (
                <p key={task.id}>
                  <Link to={`/tasks/${task.id}`}>{task.key}</Link> {task.title} <span className="badge">{statusLabel(task.status as Parameters<typeof statusLabel>[0])}</span>
                </p>
              ))}
            </>
          )}
          {detail.sourceClearedAt && <p className="muted">{t('inbox.detail.cleared', { when: fmtWhen(detail.sourceClearedAt) })}</p>}

          {canWrite && detail.status === 'needs_human' && (
            <form
              className="inbox-answer"
              onSubmit={(e) => {
                e.preventDefault();
                if (answer.trim()) void act(() => inboxApi.answer(detail.id, answer.trim()));
              }}
            >
              <label className="field">
                {t('inbox.answer.label')}
                <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} />
              </label>
              <span className="muted">{t('inbox.answer.hint')}</span>
            </form>
          )}
          {canWrite && (detail.status === 'new' || detail.status === 'needs_human') && (
            <div className="row inbox-actions" style={{ marginTop: 'var(--sp-3)' }}>
              {detail.status === 'needs_human' && (
                <button type="button" className="btn" disabled={busy || !answer.trim()} onClick={() => void act(() => inboxApi.answer(detail.id, answer.trim()))}>{t('inbox.answer.send')}</button>
              )}
              <button type="button" className="btn secondary" disabled={busy} onClick={() => void act(() => inboxApi.triage(detail.id, { status: 'done', outcome: 'reference' }))}>{t('inbox.action.reference')}</button>
              <button type="button" className="btn secondary" disabled={busy} onClick={() => void act(() => inboxApi.triage(detail.id, { status: 'trash' }))}>{t('inbox.action.trash')}</button>
            </div>
          )}
        </section>
      )}

      <div className="row filters" style={{ margin: 'var(--sp-4) 0 var(--sp-2)' }}>
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('inbox.filter.allBusinesses')}>
          <option value="">{t('inbox.filter.allBusinesses')}</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} aria-label={t('inbox.filter.allSources')}>
          <option value="">{t('inbox.filter.allSources')}</option>
          {INBOX_SOURCES.map((x) => (
            <option key={x} value={x}>{t(`inbox.source.${x}`)}</option>
          ))}
        </select>
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
        <button type="button" className={`chip${chip === 'open' ? ' on' : ''}`} onClick={() => setChip('open')} aria-pressed={chip === 'open'}>
          {t('inbox.chip.open')}
          {counts ? ` · ${counts.new + counts.needs_human}` : ''}
        </button>
        {(['new', 'needs_human'] as const).map((s) => (
          <button key={s} type="button" className={`chip${chip === s ? ' on' : ''}`} onClick={() => setChip(s)} aria-pressed={chip === s}>
            {t(`inbox.status.${s}`)}
            {counts ? ` · ${counts[s]}` : ''}
          </button>
        ))}
      </div>

      <Table columns={columns} rows={result?.rows ?? []} rowKey={(i) => i.id} empty={result === null ? t('common.loading') : t('inbox.empty')} />

      <div className="row" style={{ marginTop: 'var(--sp-3)', justifyContent: 'space-between' }}>
        <span className="muted">{t('inbox.shown', { shown: result?.rows.length ?? 0, total, page })}</span>
        {pages > 1 && (
          <span className="row">
            <button type="button" className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('inbox.page.prev')}</button>
            <button type="button" className="btn secondary small" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>{t('inbox.page.next')}</button>
          </span>
        )}
      </div>
    </div>
  );
}
